/* Native video, enhanced with the measured Winter Set control overlay. */
(() => {
  if (customElements.get('highlight-video')) return;
  class HighlightVideo extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      const video = this.querySelector('video');
      const controls = this.querySelector('[data-highlight-controls]');
      if (!video || !controls) return;
      this._connected = true;
      this._generation = (this._generation || 0) + 1;
      this.video = video;
      this.playButton = controls.querySelector('[data-highlight-play]');
      this.muteButton = controls.querySelector('[data-highlight-mute]');
      this._visible = false;
      this._userPaused = false;
      this._ignorePause = 0;
      this._pendingPlay = false;
      this._motion = matchMedia('(prefers-reduced-motion: reduce)');
      this._events = new AbortController();
      const options = { signal: this._events.signal };
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      this.dataset.enhanced = '';
      controls.hidden = false;
      video.addEventListener('play', () => { this._userPaused = false; this._sync(); }, options);
      video.addEventListener('pause', () => {
        if (this._ignorePause) this._ignorePause -= 1;
        else this._userPaused = true;
        this._sync();
      }, options);
      video.addEventListener('volumechange', () => this._sync(), options);
      this.playButton.addEventListener('click', () => {
        if (video.paused) { this._userPaused = false; this._play(false); }
        else { this._userPaused = true; video.pause(); this._sync(); }
      }, options);
      this.muteButton.addEventListener('click', () => { video.muted = !video.muted; this._sync(); }, options);
      this.addEventListener('pointerenter', e => { if (e.pointerType !== 'touch') this._showControls(); }, options);
      this.addEventListener('pointerleave', e => { if (e.pointerType !== 'touch') this.removeAttribute('data-controls-visible'); }, options);
      this.addEventListener('pointerup', e => { this._showControls(e.pointerType === 'touch'); }, options);
      this.addEventListener('focusin', () => this._showControls(), options);
      this.addEventListener('focusout', e => {
        if (!this.contains(e.relatedTarget) && !this.matches(':hover')) this.removeAttribute('data-controls-visible');
      }, options);
      document.addEventListener('visibilitychange', () => this._update(), options);
      this._onMotion = () => { this._pause(); this._update(); };
      this._motion.addEventListener('change', this._onMotion);
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(entries => {
          this._visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .1);
          this._update();
        }, { threshold: [0, .1] });
        this._observer.observe(this);
      } else { this._visible = true; this._update(); }
      this._sync();
    }
    _showControls(touch = false) {
      clearTimeout(this._hintTimer);
      this.dataset.controlsVisible = '';
      if (touch) this._hintTimer = setTimeout(() => this.removeAttribute('data-controls-visible'), 4500);
    }
    _sync() {
      this.dataset.playing = String(!this.video.paused);
      this.dataset.muted = String(this.video.muted);
      this.playButton.setAttribute('aria-label', this.video.paused ? this.playButton.dataset.playLabel : this.playButton.dataset.pauseLabel);
      this.playButton.setAttribute('aria-pressed', String(!this.video.paused));
      this.muteButton.setAttribute('aria-label', this.video.muted ? this.muteButton.dataset.unmuteLabel : this.muteButton.dataset.muteLabel);
      this.muteButton.setAttribute('aria-pressed', String(!this.video.muted));
    }
    _pause() {
      if (!this.video || this.video.paused) return;
      this._ignorePause += 1;
      this.video.pause();
    }
    _play(automatic) {
      if (this._pendingPlay) return;
      this._pendingPlay = true;
      const generation = this._generation;
      Promise.resolve(this.video.play()).then(() => {
        if (generation !== this._generation) return;
        this.removeAttribute('data-play-blocked');
        if (!this._connected || !this._visible || document.hidden || (automatic && this._motion.matches)) this._pause();
      }).catch(() => {
        if (generation === this._generation) { this._userPaused = true; this.dataset.playBlocked = ''; }
      }).finally(() => {
        if (generation === this._generation) { this._pendingPlay = false; this._sync(); }
      });
    }
    _update() {
      if (!this._connected) return;
      if (!this._visible || document.hidden) { this._pause(); return; }
      if (this.dataset.autoplay !== 'true' || this._motion.matches || navigator.connection?.saveData || this._userPaused || !this.video.paused) return;
      this._play(true);
    }
    disconnectedCallback() {
      this._connected = false;
      this._generation += 1;
      this._observer?.disconnect();
      this._events?.abort();
      this._motion?.removeEventListener('change', this._onMotion);
      clearTimeout(this._hintTimer);
      this._pause();
      if (this.video) this.video.controls = true;
      this.querySelector('[data-highlight-controls]')?.setAttribute('hidden', '');
      this.removeAttribute('data-enhanced');
      this.removeAttribute('data-controls-visible');
    }
  }
  customElements.define('highlight-video', HighlightVideo);
})();
